import {
  Box,
  Button,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
  TooltipProps,
} from "@mui/material";
import { ReactNode } from "react";

type Props = {
  title?: string;
  titleType?: "tooltip" | "bottom" | "both";
  onClick?: (e?: any) => void;
  icon?: JSX.Element;
  disabled?: boolean;
  height?: string;
  width?: string;
  children?: ReactNode;
  sx?: SxProps;
  type?: "default" | "error" | "warning";
  disabledBorder?: boolean;
  tooltipHidden?: boolean;
  disabledHoverAnimation?: boolean;
  placement?: TooltipProps["placement"];
};

const IconButtonBase = (props: Props) => {
  const theme = useTheme();

  const btnStyle = (
    type: "default" | "error" | "warning" | "icon",
    state: "default" | "hover" | "disabled" | ""
  ) => {
    const typePalette = (theme.palette.btn as any)[type];
    if (!typePalette) return {};
    switch (state) {
      case "default":
        return {
          backgroundColor: typePalette?.backgroundColor,
          border: props.disabledBorder ? "" : typePalette?.border,
          color: typePalette?.color,
        };
      case "hover":
        if (props.disabledHoverAnimation) return { backgroundColor: "" };
        return {
          backgroundColor: typePalette?.hover?.backgroundColor,
          border: props.disabledBorder ? "" : typePalette?.hover?.border,
          color: typePalette?.hover?.color,
        };
      case "disabled":
        return {
          backgroundColor: typePalette?.disabled?.backgroundColor,
          border: props.disabledBorder ? "" : typePalette?.disabled?.border,
          color: typePalette?.disabled?.color,
        };
      default:
        return {};
    }
  };

  return (
    <Button
      variant="contained"
      disabled={props.disabled}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        props.onClick(e);
      }}
      sx={{
        height: props.height,
        width: props.width,
        minWidth: props.width ? props.width : "0px",
        borderRadius: "0px",
        outline: "none",
        padding: "0px",
        backgroundColor: "#00000000",
        border: props.disabledBorder ? "" : theme.palette.btn.icon.border,
        textTransform: "none",
        boxShadow: "none",
        ...btnStyle(props.type ? props.type : "icon", "default"),
        "&:hover": {
          ...btnStyle(props.type ? props.type : "icon", "hover"),
          boxShadow: "none",
        },
        "&.Mui-disabled": {
          ...btnStyle(props.type ? props.type : "icon", "disabled"),
          boxShadow: "none",
        },
        ...props.sx,
      }}
    >
      {props.icon}
      {props.children && props.children}
    </Button>
  );
};

const Q3DIconButton = (props: Props) => {
  return props.title && props.titleType === "tooltip" ? (
    <Tooltip
      title={<span className="tool-tip-cal">{props.title}</span>}
      arrow
      placement={props.placement ?? "top"}
      disableInteractive
      disableHoverListener={props.tooltipHidden}
    >
      <span>
        <IconButtonBase
          title={props.title}
          onClick={props.onClick}
          icon={props.icon}
          disabled={props.disabled}
          height={props.height}
          width={props.width}
          type={props.type}
          sx={props.sx}
          disabledBorder={props.disabledBorder}
          disabledHoverAnimation={props.disabledHoverAnimation}
        >
          {props.children}
        </IconButtonBase>
      </span>
    </Tooltip>
  ) : props.title && props.titleType === "bottom" ? (
    <IconButtonBase
      title={props.title}
      onClick={props.onClick}
      disabled={props.disabled}
      height={props.height}
      width={props.width}
      type={props.type}
      sx={props.sx}
      disabledBorder={props.disabledBorder}
      disabledHoverAnimation={props.disabledHoverAnimation}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "2px",
        }}
      >
        {props.icon}
        <Typography sx={{ fontSize: "10px" }}>{props.title}</Typography>
      </Box>
    </IconButtonBase>
  ) : props.title && props.titleType === "both" ? (
    <Tooltip
      title={<span className="tool-tip-cal">{props.title}</span>}
      arrow
      placement={props.placement ?? "top"}
      disableInteractive
      disableHoverListener={props.tooltipHidden}
    >
      <span>
        <IconButtonBase
          title={props.title}
          onClick={props.onClick}
          disabled={props.disabled}
          height={props.height}
          width={props.width}
          type={props.type}
          sx={props.sx}
          disabledBorder={props.disabledBorder}
          disabledHoverAnimation={props.disabledHoverAnimation}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "2px",
            }}
          >
            {props.icon}
            <Typography sx={{ fontSize: "10px" }}>{props.title}</Typography>
          </Box>
        </IconButtonBase>
      </span>
    </Tooltip>
  ) : (
    <IconButtonBase
      title={props.title}
      onClick={props.onClick}
      icon={props.icon}
      disabled={props.disabled}
      height={props.height}
      width={props.width}
      sx={props.sx}
      disabledBorder={props.disabledBorder}
      disabledHoverAnimation={props.disabledHoverAnimation}
    >
      {props.children}
    </IconButtonBase>
  );
};

export default Q3DIconButton;
